import React from "react";
import { Typography } from "@kelp-dao/kernel-design-system";

/**
 * Simple Claim Component
 * Standardized UI for claiming rewards across all vaults
 */
export interface ClaimProps {
  // Claim info
  rewardTokenSymbol: string;
  claimableAmount: number;

  // State
  isConnected: boolean;
  isSupportedChain?: boolean;
  isClaimable: boolean;

  // Callbacks
  onClaim: () => void;

  // Optional
  description?: string;
  disclaimer?: React.ReactNode;
}

const Claim = ({
  rewardTokenSymbol,
  claimableAmount,
  isConnected,
  isSupportedChain = true,
  isClaimable,
  onClaim,
  description,
  disclaimer,
}: ClaimProps) => {
  const getButtonText = () => {
    if (!isConnected) return "Connect Wallet";
    if (!isSupportedChain) return "Switch Network";
    if (!isClaimable) return "Nothing to Claim";
    return `Claim ${rewardTokenSymbol}`;
  };

  const isButtonDisabled = !isConnected || !isSupportedChain || !isClaimable;

  return (
    <div className="text-sm leading-5 font-normal pt-5 flex flex-col">
      <div className="flex justify-center items-center mx-4 mb-[15px] pt-5 tablet:pt-10 px-4 tablet:px-8 pb-5 tablet:pb-10 flex-col gap-6">
        {/* Claimable Amount Display */}
        <div className="text-center">
          <Typography
            type="body"
            variant="xsmall"
            className="text-xs leading-[14.4px] font-normal text-text-secondary"
          >
            Claimable {rewardTokenSymbol}
          </Typography>
          <Typography
            type="heading"
            variant="large"
            className="text-[32px] leading-[38px] font-bold text-text-primary mt-2 font-sans"
          >
            {claimableAmount.toFixed(4)}
          </Typography>
        </div>

        {/* Description */}
        {description && (
          <Typography
            type="body"
            variant="small"
            className="text-sm leading-5 font-normal text-text-secondary text-center"
          >
            {description}
          </Typography>
        )}

        {/* Claim Button */}
        <button
          className="rounded-xl bg-text-primary p-2 px-4 h-12 min-w-[200px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onClaim}
          disabled={isButtonDisabled}
        >
          <Typography
            type="button"
            variant="default"
            className="text-base leading-6 font-semibold text-white"
          >
            {getButtonText()}
          </Typography>
        </button>

        {/* Disclaimer */}
        {disclaimer && (
          <div className="mt-4 text-center">
            <Typography
              type="body"
              variant="xsmall"
              className="text-[10px] leading-3 font-normal text-text-secondary"
            >
              {disclaimer}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default Claim;
